import {
  Button,
  PasswordInput,
  SubmitButton,
  TextInput,
  useSnackbarContext,
} from "@ameelio/ui";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { Grid, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useCurrentUserData } from "../CurrentUserProvider";
import buildPageTitle from "../lib/buildPageTitle";
import { BaseSkeleton } from "../lib/closet";
import useApolloErrorHandler from "../lib/handleApolloError";
import { ScreenTitle } from "../lib/typography";
import {
  isPassword,
  isPasswordConfirmation,
  isRequired,
} from "../lib/validate";
import { GetSystemByCodeDocument } from "./GetSystemByCode.generated";
import { InmatePasswordResetEligibilityDocument } from "./InmatePasswordResetEligibility.generated.ts";
import { UpdateInmatePasswordDocument } from "./UpdateInmatePassword.generated";

type FormData = {
  username: string;
  password: string;
  passwordConfirmation: string;
};

function InmateResetPasswordScreen() {
  const { systemCode } = useParams();
  const { t } = useTranslation();
  const handleApolloError = useApolloErrorHandler();
  const currentUserData = useCurrentUserData();
  const inmate =
    currentUserData && currentUserData?.me?.__typename === "Inmate"
      ? currentUserData.me
      : null;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormData>({
    mode: "onTouched",
    defaultValues: {
      username: "",
    },
  });

  const navigate = useNavigate();
  const snackbarContext = useSnackbarContext();

  const [inmateVerified, setInmateVerified] = useState<boolean>(false);

  // Already-authenticated inmates who were redirected here (see InmateHomeScreen)
  // are pre-verified once their user data loads, which may happen after mount.
  useEffect(() => {
    if (inmate?.enablePasswordReset) {
      setValue("username", inmate.inmateIdentification || "");
      setInmateVerified(true);
    }
  }, [inmate, setValue]);

  // We shouldn't be here if we haven't selected a system
  if (!systemCode) throw new Error("missing system code");

  const { data, loading, error } = useQuery(GetSystemByCodeDocument, {
    variables: { code: systemCode || "" },
  });

  const [updateInmatePassword] = useMutation(UpdateInmatePasswordDocument, {
    onError: handleApolloError,
    onCompleted: () => {
      snackbarContext.alert("success", t("Password updated"));
      setInmateVerified(false);
    },
    // patch cache for logged-in inmate to prevent password reset loop
    update: (cache, { data }) => {
      if (!inmate || !data?.updateInmatePassword.success) return;
      cache.modify({
        id: cache.identify(inmate),
        fields: { enablePasswordReset: () => false },
      });
    },
  });

  const [verifyInmatePasswordResetEligibility, { loading: verifyingInmate }] =
    useLazyQuery(InmatePasswordResetEligibilityDocument, {
      fetchPolicy: "network-only",
    });

  if (error) throw error;
  if (loading || !data) return <BaseSkeleton />;

  if (!data.systemByCode) return <Navigate to="/login-select" />;

  const onSubmit = async (formData: FormData) => {
    const realUsername = `${formData.username}-${data.systemByCode?.id}`;
    if (inmateVerified) {
      const { data: response } = await updateInmatePassword({
        variables: {
          input: {
            username: realUsername,
            password: formData.password,
            passwordConfirmation: formData.passwordConfirmation,
          },
        },
      });
      if (response?.updateInmatePassword.success) {
        const redirectTo = inmate ? `/` : `/login/${systemCode}`;
        navigate(redirectTo, { replace: true });
      }
    } else {
      const { data: verifyResponse, error: verifyError } =
        await verifyInmatePasswordResetEligibility({
          variables: {
            username: realUsername,
          },
        });
      if (verifyError) {
        handleApolloError(verifyError);
      } else if (verifyResponse) {
        setInmateVerified(
          verifyResponse.inmatePasswordResetEligibility.enabled
        );
      }
    }
  };

  const pageTitle = buildPageTitle(t("Reset password"));

  return (
    <>
      <title>{pageTitle}</title>
      <ScreenTitle>
        {inmateVerified
          ? t("Choose your password")
          : t("What is your username?")}
      </ScreenTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2} sx={{ marginY: 6 }}>
          <TextInput
            control={control}
            name="username"
            label={t("Username")}
            rules={isRequired(t("Please enter your username."))}
            autoComplete="username"
            autoCapitalize="false"
            disabled={inmateVerified} // don't allow editing username after verifying
            autoFocus
          />
          {inmateVerified && (
            <>
              <PasswordInput
                control={control}
                name="password"
                label={t("Password")}
                rules={isPassword()}
                autoComplete="new-password"
                helperText={t(
                  "Create a strong password with a mix of letters, numbers and symbols."
                )}
              />
              <PasswordInput
                control={control}
                name="passwordConfirmation"
                label={t("Confirm password")}
                autoComplete="off"
                rules={isPasswordConfirmation("password")}
              />
            </>
          )}
          <div>
            <Typography variant="caption">
              {t(
                "Please contact a staff member if you don't remember your Ameelio Connect username."
              )}
            </Typography>
          </div>
        </Stack>
        <Grid
          container
          sx={{
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(`/login/${systemCode}`)}
            disabled={!!inmate}
          >
            {t("Back to login")}
          </Button>
          <SubmitButton submitting={isSubmitting || verifyingInmate}>
            {inmateVerified ? t("Reset password") : t("Next")}
          </SubmitButton>
        </Grid>
      </form>
    </>
  );
}

export default InmateResetPasswordScreen;
